// ** MUI Imports
import AutoModeIcon from '@mui/icons-material/AutoMode';
import { Autocomplete, Button, FilterOptionsState, Stack, TextField, Typography, createFilterOptions } from '@mui/material';
import Grid from '@mui/material/Grid';
import _ from 'lodash';
import { matchSorter } from 'match-sorter';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import RoulettePro from 'react-roulette-pro';
import 'react-roulette-pro/dist/index.css';
import BookApi from 'src/@core/utils/api/book-api';
import { getBookCoverImage } from 'src/@core/utils/functions';

type BookRoulettePageProps = {
  tags: string[]
}

const limitFilter = createFilterOptions<string>({
  limit: 500,
});

const filterOptions = (options: string[], state: FilterOptionsState<string>) => {
  return matchSorter(limitFilter(options, state), state.inputValue);
}

const ROULETTE_BOOK_COUNT = 20
const ROULETTE_BOOK_COPIES = 10
const ROULETTE_PRIZE_INDEX = ROULETTE_BOOK_COUNT * ROULETTE_BOOK_COPIES - 10;

const normalizeTag = (tag: string): string => {
  return tag.replace(/^-/, '');
}

const BookRoulettePage = ({ tags }: BookRoulettePageProps) => {
  const router = useRouter()
  const options = useMemo<string[]>(() => tags.map(normalizeTag), [tags]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [prizeList, setPrizeList] = useState<{ id: string, image: string, bookId: string }[]>([]);
  const [startRoulette, setStartRoulette] = useState(false);

  const playRoulette = async () => {
    const books = await BookApi.getBookRoulette(selectedTags);
    setStartRoulette(false)
    let prizes = books.map(book => ({ id: '', bookId: book._id as string, image: getBookCoverImage(book) }));
    let idx = 0;
    while (prizes.length < ROULETTE_BOOK_COUNT) {
      prizes.push(prizes[idx])
      idx++;
    }
    prizes = new Array(ROULETTE_BOOK_COPIES).fill(0).map(() => [...prizes.map(book => ({ ...book, id: crypto.randomUUID() }))]).flat();
    setPrizeList(prizes)
    setTimeout(() => setStartRoulette(true), 1000)
  }

  const getRandomTags = () => {
    const size = Math.round(Math.random() * 3 + 2)
    setSelectedTags(_.sampleSize(options, size))
  }

  return (
    <>
      <Head>
        <title key={'title'}>{`Book Roulette`}</title>
      </Head>
      <Grid container spacing={6}>
        <Grid item xs={12} sx={{ paddingBottom: 4 }}>
          <Typography variant='h5'>Book Roulette</Typography>
        </Grid>
        <Grid xs={12} item>
          <Typography>Fine-tune your roulette experience by selecting tags and unveil hidden gems waiting to captivate you. Craft your literary journey with precision and uncover the gems that resonate uniquely with your preferences.</Typography>
        </Grid>
        <Grid xs={12} item>
          <Stack spacing={4}>
            <Autocomplete
              multiple
              id="tags-outlined"
              value={selectedTags}
              onChange={(_, newValue) => {
                setSelectedTags(newValue)
              }}
              filterOptions={filterOptions}
              filterSelectedOptions
              options={options}
              noOptionsText="no tag found"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="search for a tag"
                  placeholder="tags"
                />
              )}
            />
            <Stack direction={'row'} spacing={4}>
              <Typography sx={{ alignSelf: 'center' }}>Need help?</Typography>
              <Button variant='text' onClick={getRandomTags}>get some random tags</Button>
            </Stack>
          </Stack>
        </Grid>
        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant={'outlined'}
            onClick={playRoulette}
            disabled={selectedTags.length === 0}
            startIcon={<AutoModeIcon />}>
            spin the roulette
          </Button>
        </Grid>
        {prizeList.length > 0 && <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', maxHeight: '58vh' }}>
          <RoulettePro
            type={'vertical'}
            prizes={prizeList}
            prizeIndex={ROULETTE_PRIZE_INDEX}
            spinningTime={10}
            start={startRoulette}
            onPrizeDefined={() => {
              router.push(`/book/${prizeList[ROULETTE_PRIZE_INDEX].bookId}`)
            }}
          />
        </Grid>}
      </Grid>
    </>
  )
}

export const getServerSideProps = async () => {
  const tags = await BookApi.listTags();

  return { props: { tags } }
}

export default BookRoulettePage
